import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  nextDefaultFloorName,
  validateFloorInput,
  type Floor,
  type FloorInput,
  type FloorType,
} from "../../domain/floor";
import { ValidationError } from "./projectRepository";

interface FloorRow {
  id: string;
  project_id: string;
  name: string;
  floor_type: string;
  sort_order: number;
  notes: string;
  default_ceiling_height_inches: number | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapFloor(row: FloorRow): Floor {
  return {
    id: row.id,
    projectId: row.project_id,
    name: row.name,
    floorType: row.floor_type as FloorType,
    sortOrder: Number(row.sort_order),
    notes: row.notes,
    defaultCeilingHeightInches: row.default_ceiling_height_inches === null || row.default_ceiling_height_inches === undefined ? null : Number(row.default_ceiling_height_inches),
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const SELECT = `SELECT id, project_id, name, floor_type, sort_order, notes, default_ceiling_height_inches,
                       archived_at, created_at, updated_at FROM floors`;

export class FloorRepository {
  constructor(private readonly db: DbExecutor) {}

  /** All floors for a project, archived included (callers filter). */
  async listByProject(projectId: string): Promise<Floor[]> {
    const rows = await this.db.select<FloorRow>(
      `${SELECT} WHERE project_id = ? ORDER BY sort_order, created_at`,
      [projectId],
    );
    return rows.map(mapFloor);
  }

  /**
   * A project's plan needs somewhere to draw. If this project has no floor
   * at all, create "Floor 1" — otherwise return the floors it already has
   * untouched. Called when the Design workspace opens, so a new design (and
   * an old project that never had a floor) both arrive at a usable canvas
   * instead of an empty screen with no way forward.
   *
   * Idempotent and transactional: the check and the insert happen together,
   * so opening two windows cannot produce two "Floor 1"s. ARCHIVED floors
   * count as floors — a project whose only floor was archived is a
   * deliberate state, not an empty one.
   */
  async ensureFirstFloor(projectId: string): Promise<Floor | null> {
    const existing = await this.listByProject(projectId);
    if (existing.length > 0) return null;
    // The check and the insert are ONE statement: two Design tabs opening at
    // the same moment both read "no floors" before either of them writes, and
    // a project would gain two Floor 1s. Portable to PostgreSQL.
    const now = new Date().toISOString();
    const id = generateId();
    await this.db.execute(
      `INSERT INTO floors (id, project_id, name, floor_type, sort_order, notes, created_at, updated_at)
       SELECT ?, ?, ?, 'floor', 10, '', ?, ?
        WHERE NOT EXISTS (SELECT 1 FROM floors WHERE project_id = ?)`,
      [id, projectId, nextDefaultFloorName([]), now, now, projectId],
    );
    const rows = await this.listByProject(projectId);
    return rows.find((floor) => floor.id === id) ?? null;
  }

  /** The name to suggest for the next floor the user adds. */
  async suggestFloorName(projectId: string): Promise<string> {
    const existing = await this.listByProject(projectId);
    return nextDefaultFloorName(existing.map((floor) => floor.name));
  }

  async create(projectId: string, input: FloorInput): Promise<Floor> {
    const problems = validateFloorInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const existing = await this.listByProject(projectId);
    const maxSort = existing.reduce((m, f) => Math.max(m, f.sortOrder), 0);
    const now = new Date().toISOString();
    const floor: Floor = {
      id: generateId(),
      projectId,
      name: input.name.trim(),
      floorType: input.floorType,
      sortOrder: maxSort + 10,
      notes: input.notes,
      defaultCeilingHeightInches: null,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO floors (id, project_id, name, floor_type, sort_order, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        floor.id,
        floor.projectId,
        floor.name,
        floor.floorType,
        floor.sortOrder,
        floor.notes,
        floor.createdAt,
        floor.updatedAt,
      ],
    );
    return floor;
  }

  async update(id: string, input: FloorInput): Promise<void> {
    const problems = validateFloorInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.db.execute(
      "UPDATE floors SET name = ?, floor_type = ?, notes = ?, updated_at = ? WHERE id = ?",
      [input.name.trim(), input.floorType, input.notes, new Date().toISOString(), id],
    );
  }

  /**
   * Display-cascading archive: hides the floor (and thereby its subtree in
   * active views) without mutating any child records. Restore brings the
   * existing subtree back exactly as it was.
   */
  async archive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE floors SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [now, now, id],
    );
  }

  /** Phase 7: floor default ceiling / wall height (null = app default 96"). */
  async setDefaultCeilingHeight(id: string, inches: number | null): Promise<void> {
    if (inches !== null && (!Number.isInteger(inches) || inches <= 0)) throw new ValidationError(["Ceiling height must be a positive whole number of inches."]);
    await this.db.execute("UPDATE floors SET default_ceiling_height_inches = ?, updated_at = ? WHERE id = ?", [inches, new Date().toISOString(), id]);
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE floors SET archived_at = NULL, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), id],
    );
  }
}
